import { useNavigate } from "react-router-dom";
import { useTaskStore } from "@/store/task";
import { useProfileStore } from "@/store/profile";
import { useDemographicsStore } from "@/store/demographics";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { FINANCIAL_ROUTES, q2Route, type InsuranceChoice } from "@/utils/financialFlow";
import { markIntakeStepDone } from "@/store/intakeProgress";

/** Labels matching app-pwa language/en.ts */
const labels = {
  financialResponsibilityQuestion2: "Will you be using health insurance for any services at {facility}?",
  thisFacility: "this facility",
  healthInsurance: "Health Insurance",
  noInsurance: "No Insurance",
  backButton: "Back",
} as const;

const OPTIONS: { value: InsuranceChoice; label: string }[] = [
  { value: "health-insurance", label: labels.healthInsurance },
  { value: "no-insurance", label: labels.noInsurance },
];

/**
 * Second financial responsibility step: health insurance vs no insurance.
 * Matches app-pwa FinancialResponsibilityInsurance.vue.
 */
export function FinancialResponsibilityInsurancePage() {
  const navigate = useNavigate();
  const portalCaseHistoryFlow = useTaskStore((s) => s.portalCaseHistoryFlow);
  const facilityName = useProfileStore((s) => s.facilityInfo?.officeName ?? labels.thisFacility);
  const profileDob = useProfileStore((s) => s.currentPatient?.dob);
  const demoDob = useDemographicsStore((s) => s.demographics?.dob);
  /** Patient list often omits DOB; wizard demographics (just saved) usually has it. */
  const patientDob = profileDob || demoDob;
  const startNewComplaint = usePrimaryComplaintStore((s) => s.startNewComplaint);

  const question = labels.financialResponsibilityQuestion2.replace("{facility}", facilityName);

  const goBack = () => navigate(FINANCIAL_ROUTES.q1);

  const wizardSteps = useTaskStore((s) => s.wizardSteps);

  const goToChoice = (value: InsuranceChoice) => {
    // "No insurance" ends the financial section (hands off to history) → mark it done.
    if (value === "no-insurance") markIntakeStepDone("financial");
    const { path, startComplaint } = q2Route(value, {
      portalCaseHistoryFlow,
      wizardSteps,
      patientDob,
    });
    if (startComplaint) startNewComplaint();
    navigate(path);
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content financial-responsibility-content flex justify-center px-4 py-6">
        <div className="complaint-history-card financial-step-card">
          <div className="text-center mb-6">
            <p className="financial-responsibility-question text-md font-medium text-gray-800 mb-0 font-weight-bold" style={{ fontSize: "30px" }}>
              {question}
            </p>
          </div>
          <div className="flex justify-center">
            <div className="flex flex-wrap justify-evenly w-full gap-3 py-3">
              {OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="choice-btn flex-1 min-w-[140px]"
                  onClick={() => goToChoice(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={labels.backButton}>
          {labels.backButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
